class BackendAPIClient {

  constructor() {
    this.baseUrl = /** @type {any} */ (window).BACKEND_BASE_URL || "";
    this.market = /** @type {any} */ (window).market;
    this.customerId = /** @type {any} */ (window).customer?.id;
    this.customerEmail = /** @type {any} */ (window).customer?.email;
    this.accessToken = null;
    this.refreshToken = null;
    
    this.TOKENS_STORAGE_KEY = `OG_TOKENS_${this.market}`;

    this.loadCachedTokens();
  }

  loadCachedTokens() {
    try {
      const cachedTokens = localStorage.getItem(this.TOKENS_STORAGE_KEY);
      if (cachedTokens) {
        const { accessToken, refreshToken, timestamp } = JSON.parse(cachedTokens);
        const now = Date.now();
        const tokenAge = now - timestamp;

        if (tokenAge < 14.5 * 60 * 1000) { // use cached tokens if they're < 14m30s old (30s buffer)
          this.accessToken = accessToken;
          this.refreshToken = refreshToken;
        } else {
          this.clearCachedTokens();
        }
      }
    } catch (e) {
      console.warn('Failed to load cached tokens:', e);
      this.clearCachedTokens();
    }
  }

  clearCachedTokens() {
    localStorage.removeItem(this.TOKENS_STORAGE_KEY);
  }

  cacheTokens() {
    try {
      const data = {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
        timestamp: Date.now()
      };
      localStorage.setItem(this.TOKENS_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to cache tokens:', e);
    }
  }

  getBaseHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-YL-Market': this.market,
    };
  }

  getAuthHeaders() {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }
    return {
      ...this.getBaseHeaders(),
      'Authorization': `Bearer ${this.accessToken}`,
    };
  }

  isTokenExpired() {
    try {
      const { exp } = this.decodeTokenPayload();
      const now = Math.floor(Date.now() / 1000);
      return now >= exp - 30; // 30s buffer
    } catch {
      return true;
    }
  }

  decodeTokenPayload() {
    const base64 = this.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(json);
  }

  async getTokens() {
    const body = {
      id: this.customerId,
      email: this.customerEmail
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth`, {
        method: 'POST',
        headers: this.getBaseHeaders(),
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getRefreshedTokens() {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          ...this.getBaseHeaders(),
          'Authorization': `Bearer ${this.refreshToken}`,
        }
      });
      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async authenticate() {
    if (this.accessToken && this.refreshToken && !this.isTokenExpired()) return;
    if (!this.customerId || !this.customerEmail) return;

    try {
      const tokensData = await this.getTokens();
      this.accessToken = tokensData?.data?.token;
      this.refreshToken = tokensData?.data?.refresh_token;
      this.cacheTokens();

    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async refreshTokens() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const newTokensData = await this.getRefreshedTokens();
      this.accessToken = newTokensData?.data?.token;
      this.refreshToken = newTokensData?.data?.refresh_token;
      this.cacheTokens();

    } catch (e) {
      this.clearCachedTokens();
      console.error(e);
      throw (e);
    }
  }

  /**
   * @param {string} endpoint
   * @param {any} options 
   */
  async fetchWithAuth(endpoint, options={}) {
    if (this.isTokenExpired()) {
      if (this.refreshToken) {
        await this.refreshTokens();
      } else {
        throw new Error('Token expired and no refresh token available');
      }
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers
        }
      });

      if (response.status === 401 && this.refreshToken) {
        console.log('Access token expired, refreshing...');
        await this.refreshTokens();

        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          headers: {
            ...this.getAuthHeaders(),
            ...options.headers
          }
        });

        if (!retryResponse.ok) {
          throw new Error(`HTTP error: ${retryResponse.status}`);
        }
        return await retryResponse.json();
      }

      if (!response.ok) {
        throw new Error (`HTTP error: ${response.status}`);
      }
      try {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      } catch (e) {
        console.warn('Failed to parse JSON response:', e)
        return null;
      }

    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * @param {string} memberId
   */
  async getPartnerByMemberId(memberId) {
    const endpoint = `/api/members/${memberId}`;
    return this.fetchWithAuth(endpoint, { method: 'GET' });
  }

  async getLoyaltyPoints() {
    const endpoint = `/api/customers/${this.customerId}/balance`;
    return this.fetchWithAuth(endpoint, { method: 'GET' });
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  try {
    const apiClient = new BackendAPIClient();
    await apiClient.authenticate();
     /** @type {any} */ (window).backendClient = apiClient;
  } catch (e) {
    console.error('Failed to initialize API client:', e);
     /** @type {any} */ (window).backendClient = null;
  }
})